import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  TextField,
  Button,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  FormControlLabel,
  Checkbox,
  Switch,
} from '@mui/material';
import {
  Add,
  Cancel,
  Delete,
  Edit,
  Save,
  Person,
  Home,
  Phone,
  Email,
  CalendarToday,
  Clear,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getUserPersonalInfo,
  updateUserPersonalInfo,
  createUserAddress,
  updateUserAddress,
  deleteUserAddress,
  createUserRelationship,
  updateUserRelationship,
  deleteUserRelationship,
} from '../../endpoints/usersTs';
import { getPostalInfo } from '../../endpoints/lookupsTs';
import ValidatedTextField from '../../Components/ValidatedTextField';
import { emailValidator, phoneNumberValidator } from '../../utils/validators';
import { trimField } from '../../utils/strings';
import StateSelector from '../../Components/StateSelector';
import CountrySelector from '../../Components/CountrySelector';
import DateFilter from '../../Components/DateFilter';
import DateTimePicker from '../../Components/DateTimePicker';
import { addressTypes } from '../../utils/variables';
import ConfirmationDialog from '../../Components/ConfirmationDialog';
import AlertItem from '../../Components/AlertItem';
import {
  UserPersonalInfoUpdateRequest,
  UserAddressUpdateRequest,
  UserRelationshipCreateRequest,
  UserRelationshipUpdateRequest,
  Address,
  UserRelationship,
} from '../../types/users';
import { config } from '../../utils/config';
import SetTempPasswordDialog from './SetTempPasswordDialog';
import { LockReset, Lock } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface UserPIProps {
  id: string;
  refetchParent: () => void;
  canEdit: boolean;
  canAction: boolean;
  canSetTempPassword?: boolean;
  needToRefetch: boolean;
  updateNeedToRefetch: (value: boolean) => void;
}

interface PersonalInfoFormData {
  first_name: string;
  last_name: string;
  email: string;
  home_phone: string;
  cell_phone: string;
  death_date: Dayjs | null;
  bio: string;
  occupation: string;
  occupation_company: string;
  occupation_active: boolean;
  hometown: string;
  birth_date: Dayjs | null;
  update_birth_date: boolean;
  receives_virtual_newsletter: boolean;
}

interface AddressFormData {
  address_1: string;
  address_2: string;
  city: string;
  state_id: string;
  postal_code: string;
  country_id: string;
  address_type_id: string;
  start_date: Dayjs | null;
  end_date: Dayjs | null;
}

const UserPI: React.FC<UserPIProps> = ({
  id,
  refetchParent,
  canEdit,
  canAction,
  canSetTempPassword = false,
  needToRefetch,
  updateNeedToRefetch,
}) => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorSaving, setErrorSaving] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);

  // Personal Information State
  const [editingPI, setEditingPI] = useState<boolean>(false);
  const [piFormData, setPiFormData] = useState<PersonalInfoFormData>({
    first_name: '',
    last_name: '',
    email: '',
    home_phone: '',
    cell_phone: '',
    death_date: null,
    bio: '',
    occupation: '',
    occupation_company: '',
    occupation_active: true,
    hometown: '',
    birth_date: null,
    update_birth_date: false,
    receives_virtual_newsletter: false,
  });

  // Address State
  const [editingAddress, setEditingAddress] = useState<boolean>(false);
  const [addressFormData, setAddressFormData] = useState<AddressFormData>({
    address_1: '',
    address_2: '',
    city: '',
    state_id: '',
    postal_code: '',
    country_id: '',
    address_type_id: '',
    start_date: null,
    end_date: null,
  });
  const [editingAddressId, setEditingAddressId] = useState<string | null>(null);
  const [addressErrors, setAddressErrors] = useState<Record<string, boolean>>(
    {}
  );
  const [selectedCountryHasStates, setSelectedCountryHasStates] =
    useState<boolean>(false);

  // Confirmation Dialog
  const [showConfirmationDialog, setShowConfirmationDialog] =
    useState<boolean>(false);
  const [addressToRemove, setAddressToRemove] = useState<Address | null>(null);

  // Relationships State
  const [editingRelationship, setEditingRelationship] =
    useState<boolean>(false);
  const [relationshipFormData, setRelationshipFormData] = useState<{
    relation_id: string;
    first_name: string;
    last_name: string;
    birth_date: Dayjs | null;
    birth_date_updated: boolean;
    user_id: string | null;
  }>({
    relation_id: '',
    first_name: '',
    last_name: '',
    birth_date: null,
    birth_date_updated: false,
    user_id: null,
  });
  const [editingRelationshipId, setEditingRelationshipId] = useState<
    string | null
  >(null);
  const [showRelationshipConfirmation, setShowRelationshipConfirmation] =
    useState<boolean>(false);
  const [relationshipToRemove, setRelationshipToRemove] =
    useState<UserRelationship | null>(null);

  // Postal Code Lookup
  const [delayPostalCode, setDelayPostalCode] = useState<string>('');
  const [getPostalInfoActive, setGetPostalInfoActive] =
    useState<boolean>(false);
  const [loadedAddress, setLoadedAddress] = useState<boolean>(false);

  // Temp Password Dialog
  const [showTempPasswordDialog, setShowTempPasswordDialog] =
    useState<boolean>(false);

  // API Hooks
  const {
    data: userInfo,
    isLoading,
    refetch,
  } = useApiGet(['user-personal-info', id], () => getUserPersonalInfo(id), {
    enabled: !!id,
    retry: 1,
  });

  const { data: postalInfo } = useApiGet(
    ['postal-info', delayPostalCode, addressFormData.country_id],
    () => getPostalInfo(delayPostalCode, addressFormData.country_id),
    {
      enabled: getPostalInfoActive,
      retry: 1,
    }
  );

  // Mutations
  const { mutate: updatePersonalInfo } = useApiSend(
    (data: UserPersonalInfoUpdateRequest) => updateUserPersonalInfo(id, data),
    () => {
      setIsSaving(false);
      setEditingPI(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      refetch();
      refetchParent();
    },
    (error: Error) => {
      setErrorSaving(
        error.message ||
          'Error updating personal information. Please try again.'
      );
      setIsSaving(false);
    }
  );

  const { mutate: createAddress } = useApiSend(
    (data: UserAddressUpdateRequest) => createUserAddress(id, data),
    () => {
      setIsSaving(false);
      setEditingAddress(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      refetch();
      refetchParent();
    },
    (error: Error) => {
      setErrorSaving(
        error.message || 'Error creating address. Please try again.'
      );
      setIsSaving(false);
    }
  );

  const { mutate: updateAddress } = useApiSend(
    (data: { addressId: string; addressData: UserAddressUpdateRequest }) =>
      updateUserAddress(id, data.addressId, data.addressData),
    () => {
      setIsSaving(false);
      setEditingAddress(false);
      setEditingAddressId(null);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      refetch();
      refetchParent();
    },
    (error: Error) => {
      setErrorSaving(
        error.message || 'Error updating address. Please try again.'
      );
      setIsSaving(false);
    }
  );

  const { mutate: deleteAddress } = useApiSend(
    (addressId: string) => deleteUserAddress(id, addressId),
    () => {
      setIsSaving(false);
      setShowConfirmationDialog(false);
      setAddressToRemove(null);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      refetch();
      refetchParent();
    },
    (error: Error) => {
      setErrorSaving(
        error.message || 'Error deleting address. Please try again.'
      );
      setIsSaving(false);
    }
  );

  const { mutate: createRelationship } = useApiSend(
    (data: UserRelationshipCreateRequest) => createUserRelationship(id, data),
    () => {
      setIsSaving(false);
      setEditingRelationship(false);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      refetch();
      refetchParent();
    },
    (error: Error) => {
      setErrorSaving(
        error.message || 'Error creating relationship. Please try again.'
      );
      setIsSaving(false);
    }
  );

  const { mutate: updateRelationship } = useApiSend(
    (data: {
      relationshipId: string;
      relationshipData: UserRelationshipUpdateRequest;
    }) =>
      updateUserRelationship(id, data.relationshipId, data.relationshipData),
    () => {
      setIsSaving(false);
      setEditingRelationship(false);
      setEditingRelationshipId(null);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      refetch();
      refetchParent();
    },
    (error: Error) => {
      setErrorSaving(
        error.message || 'Error updating relationship. Please try again.'
      );
      setIsSaving(false);
    }
  );

  const { mutate: deleteRelationship } = useApiSend(
    (relationshipId: string) => deleteUserRelationship(id, relationshipId),
    () => {
      setIsSaving(false);
      setShowRelationshipConfirmation(false);
      setRelationshipToRemove(null);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      refetch();
      refetchParent();
    },
    (error: Error) => {
      setErrorSaving(
        error.message || 'Error deleting relationship. Please try again.'
      );
      setIsSaving(false);
    }
  );

  // Effects
  useEffect(() => {
    if (needToRefetch) {
      refetch();
      updateNeedToRefetch(false);
    }
  }, [needToRefetch, refetch, updateNeedToRefetch]);

  useEffect(() => {
    if (
      delayPostalCode &&
      delayPostalCode.length > 4 &&
      addressFormData.country_id &&
      !loadedAddress
    ) {
      setGetPostalInfoActive(true);
    } else {
      setGetPostalInfoActive(false);
    }
  }, [delayPostalCode, addressFormData.country_id, loadedAddress]);

  useEffect(() => {
    if (postalInfo) {
      if (postalInfo.city) {
        setAddressFormData((prev) => ({ ...prev, city: postalInfo.city }));
        setAddressErrors((prev) => ({ ...prev, city: false }));
      }
      if (postalInfo.state_id) {
        setAddressFormData((prev) => ({
          ...prev,
          state_id: postalInfo.state_id,
        }));
        setAddressErrors((prev) => ({ ...prev, state_id: false }));
      }
      // Only set country_id from postal lookup if no country is currently selected
      if (postalInfo.country_id && !addressFormData.country_id) {
        setAddressFormData((prev) => ({
          ...prev,
          country_id: postalInfo.country_id,
        }));
        setAddressErrors((prev) => ({ ...prev, country_id: false }));
      }
    }
  }, [postalInfo, addressFormData.country_id]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setAddressFormData((prev) => ({ ...prev, postal_code: delayPostalCode }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [delayPostalCode]);

  // Helper Functions
  const resetPersonalInfo = () => {
    setEditingPI(false);
    setPiFormData({
      first_name: '',
      last_name: '',
      email: '',
      home_phone: '',
      cell_phone: '',
      death_date: null,
      bio: '',
      occupation: '',
      occupation_company: '',
      occupation_active: true,
      hometown: '',
      birth_date: null,
      update_birth_date: false,
      receives_virtual_newsletter: false,
    });
  };

  const resetAddress = () => {
    setEditingAddress(false);
    setEditingAddressId(null);
    setAddressFormData({
      address_1: '',
      address_2: '',
      city: '',
      state_id: '',
      postal_code: '',
      country_id: '',
      address_type_id: '',
      start_date: null,
      end_date: null,
    });
    setAddressErrors({});
    setSelectedCountryHasStates(false);
    setDelayPostalCode('');
    setLoadedAddress(false);
  };

  // Personal Information Handlers
  const handleEditPI = () => {
    if (!userInfo) return;

    setEditingPI(true);
    setPiFormData({
      first_name: userInfo.first_name || '',
      last_name: userInfo.last_name || '',
      email: userInfo.email || '',
      home_phone: userInfo.home_phone || '',
      cell_phone: userInfo.cell_phone || '',
      death_date: userInfo.death_date ? dayjs(userInfo.death_date) : null,
      bio: userInfo.bio || '',
      occupation: userInfo.occupation || '',
      occupation_company: userInfo.occupation_company || '',
      occupation_active: userInfo.occupation_active ?? true,
      hometown: userInfo.hometown || '',
      birth_date: null, // Don't prefill since year is not provided to client
      update_birth_date: false,
      receives_virtual_newsletter:
        userInfo.receives_virtual_newsletter ?? false,
    });
  };

  const handleSavePI = () => {
    setIsSaving(true);
    setErrorSaving(null);

    const data: UserPersonalInfoUpdateRequest = {
      first_name: trimField(piFormData.first_name),
      last_name: trimField(piFormData.last_name),
      email: (piFormData.email && trimField(piFormData.email)) || null,
      home_phone: piFormData.home_phone?.trim() || null,
      cell_phone: piFormData.cell_phone?.trim() || null,
      death_date: piFormData.death_date
        ? piFormData.death_date.format('YYYY-MM-DD')
        : null,
      bio: piFormData.bio?.trim() || '',
      occupation: piFormData.occupation?.trim() || '',
      occupation_company: piFormData.occupation_company?.trim() || '',
      occupation_active: piFormData.occupation_active,
      hometown: piFormData.hometown?.trim() || '',
      // Only include birth_date if update_birth_date is true
      ...(piFormData.update_birth_date && {
        birth_date: piFormData.birth_date
          ? piFormData.birth_date.format('YYYY-MM-DD')
          : null,
      }),
      birth_date_updated: piFormData.update_birth_date, // Set to true if checkbox is checked
      profile_image: null, // Not implemented yet
      receives_virtual_newsletter: piFormData.receives_virtual_newsletter,
    };

    updatePersonalInfo(data);
  };

  // Address Handlers
  const handleEditAddress = (address: Address) => {
    setEditingAddress(true);
    setEditingAddressId(address.id);
    setAddressFormData({
      address_1: address.address_1,
      address_2: address.address_2,
      city: address.city,
      state_id: address.state_id,
      postal_code: address.postal_code,
      country_id: address.country_id,
      address_type_id: address.address_type_id,
      start_date: address.start_date ? dayjs(address.start_date) : null,
      end_date: address.end_date ? dayjs(address.end_date) : null,
    });
    setDelayPostalCode(address.postal_code);
    setLoadedAddress(true);
    // We'll set has_states when the country is selected in the CountrySelector
    // This will be handled by the handleCountryChange function
  };

  const handleAddAddress = () => {
    setEditingAddress(true);
    setEditingAddressId(null);

    // Determine address type based on existing addresses
    if (!userInfo?.addresses || userInfo.addresses.length === 0) {
      setAddressFormData((prev) => ({
        ...prev,
        address_type_id: addressTypes.Address,
      }));
    } else {
      // Check if primary address exists, if so, add seasonal
      const hasPrimary = userInfo.addresses.some(
        (addr) => addr.address_type_id === addressTypes.Address
      );
      setAddressFormData((prev) => ({
        ...prev,
        address_type_id: hasPrimary
          ? addressTypes.Seasonal
          : addressTypes.Address,
      }));
    }
  };

  const handleSaveAddress = () => {
    setIsSaving(true);
    setErrorSaving(null);
    setValidationErrors([]);

    // Validate required fields
    const errors: Record<string, boolean> = {};
    const errorMessages: string[] = [];

    if (!addressFormData.address_1) {
      errors.address_1 = true;
      errorMessages.push('Street address is required');
    }
    if (!addressFormData.city) {
      errors.city = true;
      errorMessages.push('City is required');
    }
    // Only require state_id if the selected country has states
    if (selectedCountryHasStates && !addressFormData.state_id) {
      errors.state_id = true;
      errorMessages.push('State is required for the selected country');
    }
    if (!addressFormData.postal_code) {
      errors.postal_code = true;
      errorMessages.push('Postal code is required');
    }
    if (!addressFormData.country_id) {
      errors.country_id = true;
      errorMessages.push('Country is required');
    }

    if (addressFormData.address_type_id === addressTypes.Seasonal) {
      if (!addressFormData.start_date) {
        errors.start_date = true;
        errorMessages.push('Start date is required for seasonal addresses');
      }
      if (!addressFormData.end_date) {
        errors.end_date = true;
        errorMessages.push('End date is required for seasonal addresses');
      }
    }

    setAddressErrors(errors);

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errorMessages);
      setIsSaving(false);
      return;
    }

    const data: UserAddressUpdateRequest = {
      address_1: addressFormData.address_1?.trim() || '',
      address_2: addressFormData.address_2?.trim() || '',
      city: addressFormData.city?.trim() || '',
      state_id: addressFormData.state_id || '',
      postal_code: addressFormData.postal_code?.trim() || '',
      country_id: addressFormData.country_id || '',
      address_type_id: addressFormData.address_type_id,
      start_date: addressFormData.start_date
        ? addressFormData.start_date.format('YYYY-MM-DD')
        : null,
      end_date: addressFormData.end_date
        ? addressFormData.end_date.format('YYYY-MM-DD')
        : null,
    };

    if (editingAddressId) {
      updateAddress({ addressId: editingAddressId, addressData: data });
    } else {
      createAddress(data);
    }
  };

  const handleConfirmRemoveAddress = (address: Address) => {
    setAddressToRemove(address);
    setShowConfirmationDialog(true);
  };

  const handleRemoveAddress = () => {
    if (addressToRemove) {
      deleteAddress(addressToRemove.id);
    }
  };

  const handleCancelRemoveAddress = () => {
    setAddressToRemove(null);
    setShowConfirmationDialog(false);
  };

  const handlePostalCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLoadedAddress(false);
    const value = event.target.value;
    setDelayPostalCode(value);
    if (value.length > 0) {
      setAddressErrors((prev) => ({ ...prev, postal_code: false }));
      setValidationErrors([]);
    }
  };

  const handleCountryChange = (value: any) => {
    if (value) {
      if (value.id !== addressFormData.country_id) {
        setAddressFormData((prev) => ({ ...prev, state_id: '' }));
      }
      setAddressFormData((prev) => ({ ...prev, country_id: value.id }));
      setAddressErrors((prev) => ({ ...prev, country_id: false }));
      // Set the has_states property from the selected country
      setSelectedCountryHasStates(value.has_states || false);
      setValidationErrors([]);
    } else {
      setAddressFormData((prev) => ({ ...prev, country_id: '' }));
      setSelectedCountryHasStates(false);
      setValidationErrors([]);
    }
  };

  const handleStateChange = (value: any) => {
    if (value) {
      setAddressFormData((prev) => ({ ...prev, state_id: value.id }));
      setAddressErrors((prev) => ({ ...prev, state_id: false }));
      setValidationErrors([]);
    } else {
      setAddressFormData((prev) => ({ ...prev, state_id: '' }));
      setValidationErrors([]);
    }
  };

  const handleRouteToSeeking = () => {
    window.location.href = `/seeking-transfer/create/${id}`;
  };

  // Relationship Handlers
  const handleEditRelationship = (relationship: UserRelationship) => {
    setEditingRelationship(true);
    setEditingRelationshipId(relationship.id);
    setRelationshipFormData({
      relation_id: relationship.relation_id,
      first_name: relationship.first_name,
      last_name: relationship.last_name || '',
      birth_date: null, // Don't prefill since we don't have the full date
      birth_date_updated: false,
      user_id: relationship.is_member ? relationship.id : null,
    });
  };

  const handleAddRelationship = () => {
    setEditingRelationship(true);
    setEditingRelationshipId(null);
    setRelationshipFormData({
      relation_id: '',
      first_name: '',
      last_name: '',
      birth_date: null,
      birth_date_updated: false,
      user_id: null,
    });
  };

  const handleSaveRelationship = () => {
    setIsSaving(true);
    setErrorSaving(null);

    if (!relationshipFormData.relation_id || !relationshipFormData.first_name) {
      setErrorSaving('Relation type and first name are required');
      setIsSaving(false);
      return;
    }

    const data = {
      relation_id: relationshipFormData.relation_id,
      first_name: trimField(relationshipFormData.first_name),
      last_name:
        (relationshipFormData.last_name &&
          trimField(relationshipFormData.last_name)) ||
        null,
      birth_date: relationshipFormData.birth_date_updated
        ? relationshipFormData.birth_date
          ? relationshipFormData.birth_date.format('YYYY-MM-DD')
          : null
        : null,
      birth_date_updated: relationshipFormData.birth_date_updated,
      user_id: relationshipFormData.user_id,
    };

    if (editingRelationshipId) {
      updateRelationship({
        relationshipId: editingRelationshipId,
        relationshipData: data,
      });
    } else {
      createRelationship(data);
    }
  };

  const handleConfirmRemoveRelationship = (relationship: UserRelationship) => {
    setRelationshipToRemove(relationship);
    setShowRelationshipConfirmation(true);
  };

  const handleRemoveRelationship = () => {
    if (relationshipToRemove) {
      deleteRelationship(relationshipToRemove.id);
    }
  };

  const handleCancelRemoveRelationship = () => {
    setRelationshipToRemove(null);
    setShowRelationshipConfirmation(false);
  };

  const resetRelationship = () => {
    setEditingRelationship(false);
    setEditingRelationshipId(null);
    setRelationshipFormData({
      relation_id: '',
      first_name: '',
      last_name: '',
      birth_date: null,
      birth_date_updated: false,
      user_id: null,
    });
  };

  // Sort addresses by type (primary first)
  const sortedAddresses = userInfo?.addresses?.sort((a, b) => {
    if (a.address_type_id === addressTypes.Address) return -1;
    if (b.address_type_id === addressTypes.Address) return 1;
    return 0;
  });

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography variant="h6" color="text.secondary">
          Loading personal information...
        </Typography>
      </Box>
    );
  }

  if (!userInfo) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography variant="h6" color="text.secondary">
          User not found
        </Typography>
      </Box>
    );
  }

  // Partial user view (limited permissions)
  if (userInfo.is_partial) {
    return (
      <Box sx={{ p: 3 }}>
        <Card elevation={2}>
          <CardHeader
            title="Personal Information"
            avatar={<Person color="primary" />}
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>First Name:</strong> {userInfo.first_name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Last Name:</strong> {userInfo.last_name}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Is Member?:</strong>{' '}
                  {userInfo.last_chapter ? 'Yes' : 'No'}
                </Typography>
              </Grid>
              {userInfo.last_chapter && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">
                    <strong>Current/Last Chapter:</strong>{' '}
                    {userInfo.last_chapter}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Typography variant="body1">
                  <strong>Member ID:</strong> {userInfo.imis_id || 'N/A'}
                </Typography>
              </Grid>
              {userInfo.last_chapter && (
                <Grid item xs={12} md={6}>
                  <Typography variant="body1">
                    <strong>Active Member:</strong>{' '}
                    {userInfo.member_active ? 'Yes' : 'No'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Determine if seeking transfer button should be shown
  const showSeekingTransfer =
    config.SEEKING_TRANSFER_ENABLED &&
    userInfo?.last_chapter &&
    !userInfo?.death_date &&
    (canAction || userInfo?.id === localStorage.getItem('user_id'));

  return (
    <Box sx={{ p: 3 }}>
      {showSuccessMessage && (
        <Box sx={{ mb: 2 }}>
          <AlertItem
            severity="success"
            description="Information updated successfully!"
            onClose={() => setShowSuccessMessage(false)}
            keepOpen={false}
          />
        </Box>
      )}
      {errorSaving && (
        <Box sx={{ mb: 2 }}>
          <AlertItem
            severity="error"
            description={errorSaving}
            onClose={() => setErrorSaving(null)}
            keepOpen={true}
          />
        </Box>
      )}
      {validationErrors.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <AlertItem
            severity="error"
            description={
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Please fix the following errors:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </>
            }
            onClose={() => setValidationErrors([])}
            keepOpen={true}
          />
        </Box>
      )}

      {showSeekingTransfer && (
        <Box sx={{ mb: 2, textAlign: 'right' }}>
          {userInfo.transfer_request_id ? (
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={() =>
                (window.location.href = `/seeking-transfer/${userInfo.transfer_request_id}`)
              }
            >
              View Transfer
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={handleRouteToSeeking}
            >
              Seek Transfer
            </Button>
          )}
        </Box>
      )}

      {/* Personal Information Card */}
      <Card elevation={2} sx={{ mb: 3 }}>
        <CardHeader
          title="Personal Information"
          avatar={<Person color="primary" />}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Change Password button - shown when user is viewing their own profile */}
              {userInfo?.id === localStorage.getItem('user_id') && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Lock />}
                  onClick={() => navigate('/change-password')}
                  disabled={isSaving}
                  color="primary"
                >
                  Change Password
                </Button>
              )}
              {/* Set Temp Password button - shown when admin has permission */}
              {canSetTempPassword && canAction && (
                <>
                  {userInfo?.has_temp_password && (
                    <Chip
                      label="Temp Password Set"
                      color="warning"
                      size="small"
                      sx={{ fontSize: '0.75rem' }}
                    />
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<LockReset />}
                    onClick={() => setShowTempPasswordDialog(true)}
                    disabled={isSaving}
                    color="warning"
                  >
                    Set Temp Password
                  </Button>
                </>
              )}
            </Box>
          }
        />
        <CardContent>
          <Grid container spacing={2}>
            {editingPI ? (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="First Name"
                    value={piFormData.first_name}
                    onChange={(e) =>
                      setPiFormData((prev) => ({
                        ...prev,
                        first_name: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Last Name"
                    value={piFormData.last_name}
                    onChange={(e) =>
                      setPiFormData((prev) => ({
                        ...prev,
                        last_name: e.target.value,
                      }))
                    }
                    fullWidth
                    size="small"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ValidatedTextField
                    label="Email"
                    validator={emailValidator}
                    onChange={(error, value) =>
                      setPiFormData((prev) => ({ ...prev, email: value }))
                    }
                    value={piFormData.email}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ValidatedTextField
                    label="Home Phone"
                    validator={phoneNumberValidator}
                    onChange={(error, value) =>
                      setPiFormData((prev) => ({ ...prev, home_phone: value }))
                    }
                    value={piFormData.home_phone}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <ValidatedTextField
                    label="Cell Phone"
                    validator={phoneNumberValidator}
                    onChange={(error, value) =>
                      setPiFormData((prev) => ({ ...prev, cell_phone: value }))
                    }
                    value={piFormData.cell_phone}
                  />
                </Grid>
                {userInfo.last_chapter && canEdit && (
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DateTimePicker
                        date={piFormData.death_date}
                        label="Death Date"
                        required={false}
                        minDate={null}
                        maxDate={dayjs()}
                        handleDateChange={(value) =>
                          setPiFormData((prev) => ({
                            ...prev,
                            death_date: value,
                          }))
                        }
                        handleErrorUpdate={() => {}}
                        extraError={false}
                        extraErrorMessage=""
                        disableFuture={true}
                      />
                      {piFormData.death_date && (
                        <IconButton
                          size="small"
                          onClick={() =>
                            setPiFormData((prev) => ({
                              ...prev,
                              death_date: null,
                            }))
                          }
                          color="error"
                        >
                          <Clear />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                )}
                {userInfo.last_chapter && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Bio"
                      value={piFormData.bio}
                      onChange={(e) =>
                        setPiFormData((prev) => ({
                          ...prev,
                          bio: e.target.value,
                        }))
                      }
                      fullWidth
                      multiline
                      rows={3}
                      size="small"
                    />
                  </Grid>
                )}
                {userInfo.last_chapter && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Occupation"
                      value={piFormData.occupation}
                      onChange={(e) =>
                        setPiFormData((prev) => ({
                          ...prev,
                          occupation: e.target.value,
                        }))
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                )}
                {userInfo.last_chapter && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Company"
                      value={piFormData.occupation_company}
                      onChange={(e) =>
                        setPiFormData((prev) => ({
                          ...prev,
                          occupation_company: e.target.value,
                        }))
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                )}
                {userInfo.last_chapter && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Hometown"
                      value={piFormData.hometown}
                      onChange={(e) =>
                        setPiFormData((prev) => ({
                          ...prev,
                          hometown: e.target.value,
                        }))
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                )}

                {canEdit && userInfo.member_active && !userInfo.death_date && (
                  <Grid item xs={12} md={6}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>The P.E.O. Record</strong>
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" color="text.secondary">
                          Hard Copy
                        </Typography>
                        <Switch
                          checked={piFormData.receives_virtual_newsletter}
                          onChange={(e) =>
                            setPiFormData((prev) => ({
                              ...prev,
                              receives_virtual_newsletter: e.target.checked,
                            }))
                          }
                          color="primary"
                        />
                        <Typography variant="body2" color="text.secondary">
                          Digital
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                )}
                {userInfo.last_chapter && (
                  <Grid item xs={12} md={6}>
                    <Box>
                      <Box mt={1} mb={1}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={piFormData.update_birth_date}
                              onChange={(e) => {
                                setPiFormData((prev) => ({
                                  ...prev,
                                  update_birth_date: e.target.checked,
                                  birth_date: e.target.checked
                                    ? prev.birth_date
                                    : null,
                                }));
                              }}
                            />
                          }
                          label="Update Birth Date"
                        />
                      </Box>
                      {piFormData.update_birth_date && (
                        <>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mb={1}
                          >
                            <DateFilter
                              label="Birth Date"
                              filterValue={piFormData.birth_date}
                              updateFilters={(value) =>
                                setPiFormData((prev) => ({
                                  ...prev,
                                  birth_date: value,
                                }))
                              }
                              disabled={false}
                            />
                            {piFormData.birth_date && (
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setPiFormData((prev) => ({
                                    ...prev,
                                    birth_date: null,
                                  }))
                                }
                                color="error"
                              >
                                <Clear />
                              </IconButton>
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.75rem' }}
                          >
                            Note: Only month and date will be used. Year is for
                            general information only.
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Grid>
                )}
              </>
            ) : (
              <>
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Person color="action" />
                      <Typography variant="h6">
                        {userInfo.first_name} {userInfo.last_name}
                      </Typography>
                    </Box>
                    <Box display="flex" gap={1}>
                      <Chip
                        label={userInfo.last_chapter ? 'Member' : 'Non-Member'}
                        color={userInfo.last_chapter ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                      {userInfo.last_chapter && userInfo.death_date && (
                        <Chip
                          label="Deceased"
                          color="default"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {userInfo.last_chapter && !userInfo.death_date && (
                        <Chip
                          label={userInfo.member_active ? 'Active' : 'Inactive'}
                          color={userInfo.member_active ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {userInfo.expelled && (
                        <Chip
                          label="Expelled"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {userInfo.last_chapter && (
                        <Chip
                          label={`Chapter: ${userInfo.last_chapter}`}
                          color="primary"
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {userInfo.imis_id && (
                        <Chip
                          label={`ID: ${userInfo.imis_id}`}
                          color="info"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>

                {userInfo.email && (
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Email color="action" />
                      <Typography variant="body1">
                        <strong>Email:</strong> {userInfo.email}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {userInfo.home_phone && (
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Phone color="action" />
                      <Typography variant="body1">
                        <strong>Home Phone:</strong> {userInfo.home_phone}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {userInfo.cell_phone && (
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Phone color="action" />
                      <Typography variant="body1">
                        <strong>Cell Phone:</strong> {userInfo.cell_phone}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {userInfo.birth_date && (
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday color="action" />
                      <Typography variant="body1">
                        <strong>Birth Date:</strong> {userInfo.birth_date}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {userInfo.death_date && (
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarToday color="action" />
                      <Typography variant="body1">
                        <strong>Death Date:</strong>{' '}
                        {dayjs(userInfo.death_date).format('MMM D, YYYY')}
                      </Typography>
                    </Box>
                  </Grid>
                )}
                {userInfo.bio && (
                  <Grid item xs={12}>
                    <Typography variant="body1">
                      <strong>Bio:</strong> {userInfo.bio}
                    </Typography>
                  </Grid>
                )}
                {userInfo.occupation && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1">
                      <strong>Occupation:</strong> {userInfo.occupation}
                    </Typography>
                  </Grid>
                )}
                {userInfo.occupation_company && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1">
                      <strong>Company:</strong> {userInfo.occupation_company}
                    </Typography>
                  </Grid>
                )}
                {userInfo.occupation && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1">
                      <strong>Occupation Active:</strong>{' '}
                      {userInfo.occupation_active ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                )}
                {userInfo.hometown && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1">
                      <strong>Hometown:</strong> {userInfo.hometown}
                    </Typography>
                  </Grid>
                )}
                {canEdit && userInfo.member_active && !userInfo.death_date && (
                  <Grid item xs={12} md={6}>
                    <Typography variant="body1">
                      <strong>The P.E.O. Record:</strong>{' '}
                      {userInfo.receives_virtual_newsletter
                        ? 'Digital'
                        : 'Hard Copy'}
                    </Typography>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
          {editingPI ? (
            <>
              <Button
                variant="contained"
                size="small"
                startIcon={<Cancel />}
                onClick={resetPersonalInfo}
                disabled={isSaving}
                color="warning"
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<Save />}
                onClick={handleSavePI}
                disabled={
                  isSaving || !piFormData.first_name || !piFormData.last_name
                }
                color="success"
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </>
          ) : (
            canEdit && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Edit />}
                onClick={handleEditPI}
                disabled={isSaving}
                color="info"
              >
                Edit
              </Button>
            )
          )}
        </CardActions>
      </Card>

      {/* Addresses Section */}
      <Card elevation={2}>
        <CardHeader
          title="Addresses"
          avatar={<Home color="primary" />}
          action={
            canEdit &&
            userInfo.addresses &&
            userInfo.addresses.length < 2 && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={handleAddAddress}
                disabled={isSaving}
                color="success"
              >
                Add Address
              </Button>
            )
          }
        />
        <CardContent>
          {!userInfo.addresses || userInfo.addresses.length === 0 ? (
            <Box textAlign="center" py={3}>
              <Typography variant="body2" color="text.secondary">
                No addresses available
              </Typography>
              {canEdit && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddAddress}
                  sx={{ mt: 1 }}
                >
                  Add First Address
                </Button>
              )}
            </Box>
          ) : (
            <Grid container spacing={2}>
              {sortedAddresses?.map((address) => (
                <Grid item xs={12} md={6} key={address.id}>
                  <Card variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6" mb={1}>
                      {address.address_type_id === addressTypes.Address
                        ? 'Primary'
                        : 'Seasonal'}
                    </Typography>
                    <Stack spacing={1} mb={2}>
                      <Typography variant="body2">
                        <strong>Street:</strong> {address.address_1}
                      </Typography>
                      {address.address_2 && (
                        <Typography variant="body2">
                          <strong>Street 2:</strong> {address.address_2}
                        </Typography>
                      )}
                      <Typography variant="body2">
                        <strong>City:</strong> {address.city}
                      </Typography>
                      <Typography variant="body2">
                        <strong>State/Province:</strong> {address.state}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Postal Code:</strong> {address.postal_code}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Country:</strong> {address.country}
                      </Typography>
                      {address.start_date && address.end_date ? (
                        <Typography variant="body2">
                          <strong>Dates:</strong>{' '}
                          {`${dayjs(address.start_date).format('MMM D')} - ${dayjs(address.end_date).format('MMM D')}`}
                        </Typography>
                      ) : (
                        <Typography variant="body2">
                          <strong>Timing:</strong> Year Round
                        </Typography>
                      )}
                    </Stack>
                    {canEdit && (
                      <Box display="flex" gap={1} justifyContent="flex-end">
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => handleEditAddress(address)}
                          disabled={isSaving}
                          color="info"
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Delete />}
                          onClick={() => handleConfirmRemoveAddress(address)}
                          disabled={isSaving}
                          color="error"
                        >
                          Remove
                        </Button>
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>

      {/* Relationships Card - Only show for members */}
      {userInfo.last_chapter && (
        <Card elevation={2} sx={{ mt: 3 }}>
          <CardHeader
            title="Relationships"
            avatar={<Person color="primary" />}
            action={
              canEdit && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddRelationship}
                  disabled={isSaving}
                  color="success"
                >
                  Add Relationship
                </Button>
              )
            }
          />
          <CardContent>
            {!userInfo.relationships || userInfo.relationships.length === 0 ? (
              <Box textAlign="center" py={3}>
                <Typography variant="body2" color="text.secondary">
                  No relationships available
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {userInfo.relationships.map((relationship) => (
                  <Grid item xs={12} md={6} key={relationship.id}>
                    <Card variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="h6" mb={1}>
                        {relationship.relation}
                      </Typography>
                      <Stack spacing={1} mb={2}>
                        <Typography variant="body2">
                          <strong>Name:</strong> {relationship.first_name}{' '}
                          {relationship.last_name}
                        </Typography>
                        {relationship.age && (
                          <Typography variant="body2">
                            <strong>Age:</strong> {relationship.age}
                          </Typography>
                        )}
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2">
                            <strong>Member:</strong>
                          </Typography>
                          <Chip
                            label={relationship.is_member ? 'Yes' : 'No'}
                            color={
                              relationship.is_member ? 'success' : 'default'
                            }
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        {relationship.is_member &&
                          relationship.chapter_name && (
                            <Typography variant="body2">
                              <strong>Chapter:</strong>{' '}
                              {relationship.chapter_name}
                            </Typography>
                          )}
                      </Stack>
                      {canEdit && (
                        <Box display="flex" gap={1} justifyContent="flex-end">
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Edit />}
                            onClick={() => handleEditRelationship(relationship)}
                            disabled={isSaving}
                            color="info"
                          >
                            Edit
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Delete />}
                            onClick={() =>
                              handleConfirmRemoveRelationship(relationship)
                            }
                            disabled={isSaving}
                            color="error"
                          >
                            Remove
                          </Button>
                        </Box>
                      )}
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {/* Address Edit Dialog */}
      <Dialog
        open={editingAddress}
        onClose={resetAddress}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingAddressId ? 'Edit Address' : 'Add New Address'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <CountrySelector
                value={addressFormData.country_id}
                handleChange={handleCountryChange}
                size="small"
                error={addressErrors.country_id}
                errorMessage={
                  addressErrors.country_id ? 'Country is required' : ''
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Postal Code"
                value={delayPostalCode}
                onChange={handlePostalCodeChange}
                fullWidth
                size="small"
                required
                error={addressErrors.postal_code}
                helperText={
                  addressErrors.postal_code ? 'Postal Code is required' : ''
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Street"
                value={addressFormData.address_1}
                onChange={(e) => {
                  setAddressFormData((prev) => ({
                    ...prev,
                    address_1: e.target.value,
                  }));
                  setAddressErrors((prev) => ({ ...prev, address_1: false }));
                  setValidationErrors([]);
                }}
                fullWidth
                size="small"
                required
                error={addressErrors.address_1}
                helperText={addressErrors.address_1 ? 'Street is required' : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Street 2"
                value={addressFormData.address_2}
                onChange={(e) =>
                  setAddressFormData((prev) => ({
                    ...prev,
                    address_2: e.target.value,
                  }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="City"
                value={addressFormData.city}
                onChange={(e) => {
                  setAddressFormData((prev) => ({
                    ...prev,
                    city: e.target.value,
                  }));
                  setAddressErrors((prev) => ({ ...prev, city: false }));
                  setValidationErrors([]);
                }}
                fullWidth
                size="small"
                required
                error={addressErrors.city}
                helperText={addressErrors.city ? 'City is required' : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <StateSelector
                value={addressFormData.state_id}
                handleChange={handleStateChange}
                countryId={addressFormData.country_id}
                size="small"
                required={selectedCountryHasStates}
                error={addressErrors.state_id}
                errorMessage={addressErrors.state_id ? 'State is required' : ''}
                disabled={
                  !addressFormData.country_id || !selectedCountryHasStates
                }
              />
            </Grid>
            {addressFormData.address_type_id === addressTypes.Seasonal && (
              <>
                <Grid item xs={12} md={6}>
                  <DateFilter
                    label="Start Date"
                    filterValue={addressFormData.start_date}
                    updateFilters={(value) => {
                      setAddressFormData((prev) => ({
                        ...prev,
                        start_date: value,
                      }));
                      setAddressErrors((prev) => ({
                        ...prev,
                        start_date: false,
                      }));
                    }}
                    views={['month', 'day']}
                    error={addressErrors.start_date}
                    errorMessage={
                      addressErrors.start_date ? 'Start Date is required' : ''
                    }
                    required={true}
                    disabled={false}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DateFilter
                    label="End Date"
                    filterValue={addressFormData.end_date}
                    updateFilters={(value) => {
                      setAddressFormData((prev) => ({
                        ...prev,
                        end_date: value,
                      }));
                      setAddressErrors((prev) => ({
                        ...prev,
                        end_date: false,
                      }));
                    }}
                    views={['month', 'day']}
                    error={addressErrors.end_date}
                    errorMessage={
                      addressErrors.end_date ? 'End Date is required' : ''
                    }
                    required={true}
                    disabled={false}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={resetAddress}
            disabled={isSaving}
            color="warning"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveAddress}
            variant="contained"
            disabled={isSaving}
            color="success"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Relationship Edit Dialog */}
      <Dialog
        open={editingRelationship}
        onClose={resetRelationship}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingRelationshipId ? 'Edit Relationship' : 'Add New Relationship'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Relationship Type</InputLabel>
                <Select
                  value={relationshipFormData.relation_id}
                  onChange={(e) =>
                    setRelationshipFormData((prev) => ({
                      ...prev,
                      relation_id: e.target.value,
                    }))
                  }
                  label="Relationship Type"
                >
                  <MenuItem value="a716cf6c8c024906856e0601977a7f01">
                    Spouse
                  </MenuItem>
                  <MenuItem value="ced6b659789c4f9f9a4a5d504e4c88d0">
                    Child
                  </MenuItem>
                  <MenuItem value="9945301edae6415ca6c32b5f200c3818">
                    Parent
                  </MenuItem>
                  <MenuItem value="8649a328e4aa4e61bd4f9a51d94cef34">
                    Sibling
                  </MenuItem>
                  <MenuItem value="0467fdc5c82a49c2b930a17002fbbf08">
                    Pet
                  </MenuItem>
                  <MenuItem value="3ff84742a1ec44f2b27ad2ac0e7f7e4a">
                    Other
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="First Name"
                value={relationshipFormData.first_name}
                onChange={(e) =>
                  setRelationshipFormData((prev) => ({
                    ...prev,
                    first_name: e.target.value,
                  }))
                }
                fullWidth
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Last Name"
                value={relationshipFormData.last_name}
                onChange={(e) =>
                  setRelationshipFormData((prev) => ({
                    ...prev,
                    last_name: e.target.value,
                  }))
                }
                fullWidth
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <DateFilter
                    label="Birth Date"
                    filterValue={relationshipFormData.birth_date}
                    updateFilters={(value) =>
                      setRelationshipFormData((prev) => ({
                        ...prev,
                        birth_date: value,
                      }))
                    }
                    disabled={!relationshipFormData.birth_date_updated}
                  />
                  {relationshipFormData.birth_date &&
                    relationshipFormData.birth_date_updated && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          setRelationshipFormData((prev) => ({
                            ...prev,
                            birth_date: null,
                          }))
                        }
                        color="error"
                      >
                        <Clear />
                      </IconButton>
                    )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.75rem' }}
                >
                  Note: Only month and date will be used. Year is for general
                  information only.
                </Typography>
                <Box mt={1}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={relationshipFormData.birth_date_updated}
                        onChange={(e) => {
                          setRelationshipFormData((prev) => ({
                            ...prev,
                            birth_date_updated: e.target.checked,
                            birth_date: e.target.checked
                              ? prev.birth_date
                              : null,
                          }));
                        }}
                      />
                    }
                    label="Update Birth Date"
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={resetRelationship}
            disabled={isSaving}
            color="warning"
            variant="contained"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRelationship}
            variant="contained"
            disabled={
              isSaving ||
              !relationshipFormData.relation_id ||
              !relationshipFormData.first_name
            }
            color="success"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={showConfirmationDialog}
        handleCancel={handleCancelRemoveAddress}
        handleOk={handleRemoveAddress}
        title="Remove Address"
        message="Are you sure you want to remove this address?"
        confirmText="Remove"
        cancelText="Cancel"
      />

      {/* Relationship Confirmation Dialog */}
      <ConfirmationDialog
        open={showRelationshipConfirmation}
        handleCancel={handleCancelRemoveRelationship}
        handleOk={handleRemoveRelationship}
        title="Remove Relationship"
        message="Are you sure you want to remove this relationship?"
        confirmText="Remove"
        cancelText="Cancel"
      />

      {/* Set Temporary Password Dialog */}
      {canSetTempPassword && canAction && (
        <SetTempPasswordDialog
          open={showTempPasswordDialog}
          onClose={() => setShowTempPasswordDialog(false)}
          userId={id}
          userName={
            userInfo
              ? `${userInfo.first_name} ${userInfo.last_name}`
              : 'this user'
          }
          onSuccess={() => {
            refetch();
            refetchParent();
          }}
        />
      )}
    </Box>
  );
};

export default UserPI;
